// src/client/entity-client.ts
import { BaseClient } from "./base.client.js";
import { FieldBuilder } from "./field-builder.client.js";
import type { DocumentNode } from "graphql";
import type {
  ClientConfig,
  FindManyArgs,
  FindUniqueArgs,
  Operation,
  CreateArgs,
  UpdateArgs,
  DeleteArgs,
  FieldSelection,
  QueryOptions,
} from "./types.js";

export type SelectableField = {
  [key: string]: boolean | SelectableField;
};

export type EntityConfig = {
  fields: readonly string[]; // Make this readonly
  relations: {
    [key: string]: {
      fields?: readonly string[];
      defaultInclude?: boolean;
    };
  };
};

/**
 * Base class for entity-specific clients with CRUD operations
 * @template TEntity - The entity type (e.g., User, Post)
 * @template TInsertInput - The input type for create operations
 * @template TSetInput - The input type for update operations
 * @template TWhereInput - The variables type for operations
 */
export abstract class EntityClient<
  TEntity,
  TInsertInput,
  TSetInput, // Input type for update operations (e.g., UserUpdateInput)
  TWhereInput,
> extends BaseClient {
  protected entityName: string;
  protected config: EntityConfig;

  constructor(
    config: ClientConfig,
    entityName: string,
    entityConfig: EntityConfig
  ) {
    super(config);
    this.entityName = entityName;
    this.config = entityConfig;
  }

  protected buildFieldSelection(
    selection: FieldSelection<TEntity> = {}
  ): string {
    const builder = new FieldBuilder();

    // Process field selection
    Object.entries(selection).forEach(([field, value]) => {
      if (value === true) {
        builder.select(field);
      } else if (typeof value === "object") {
        builder.include(field, (subBuilder) => {
          const subSelection = value as SelectableField;
          if (subSelection.select) {
            Object.entries(subSelection.select).forEach(
              ([subField, selected]) => {
                if (selected) subBuilder.select(subField);
              }
            );
          }
        });
      }
    });

    return builder.buildSelection();
  }

  /**
   * Find multiple entities matching the given criteria
   */
  async findMany(
    args: FindManyArgs<TEntity> = {}
  ): Promise<TEntity[]> {
    const subSelection = args as { select?: FieldSelection<TEntity> };
    const operation = {
      key: `${this.entityName}.findMany`,
      document: this.getFindManyDocument(
        this.buildFieldSelection(subSelection.select)
      ),
    };

    const result = await this.executeQuery<{ [key: string]: TEntity[] }>(
      operation,
      {
        variables: {
          where: args.where,
          orderBy: args.orderBy,
          take: args.take,
          skip: args.skip,
        },
        ...args.options,
      }
    );

    return result[this.entityName.toLowerCase()];
  }

  async findUnique(
    args: FindUniqueArgs<TEntity>
  ): Promise<TEntity | null> {
    const operation = {
      key: `${this.entityName}.findUnique`,
      document: this.getFindUniqueDocument(
        this.buildFieldSelection(args.select)
      ),
    };

    const result = await this.executeQuery<{ [key: string]: TEntity | null }>(
      operation,
      {
        variables: { where: args.where },
        ...args.options,
      }
    );

    return result[this.entityName.toLowerCase()];
  }

  /**
   * Find a unique entity by its identifier
   */

  /**
   * Create a new entity
   */
  async create(args: CreateArgs<TInsertInput, TEntity>): Promise<TEntity> {
    const subSelection = args as { select?: FieldSelection<TEntity> };
    const operation = {
      key: `${this.entityName}.create`,
      document: this.getCreateDocument(
        this.buildFieldSelection(subSelection.select)
      ),
    };

    const result = await this.executeQuery<{ [key: string]: TEntity }>(
      operation,
      {
        variables: { data: args.data },
        ...args.options,
      }
    );

    return result[this.getResponseKey("create")];
  }

  /**
   * Update an existing entity
   */
  async update(args: UpdateArgs<TSetInput, TWhereInput>): Promise<TEntity> {
    const subSelection = args as { select?: FieldSelection<TEntity> };
    const operation = {
      key: `${this.entityName}.update`,
      document: this.getUpdateDocument(
        this.buildFieldSelection(subSelection.select)
      ),
    };

    const result = await this.executeQuery<{ [key: string]: TEntity }>(
      operation,
      {
        variables: {
          where: args.where,
          data: args.data,
        },
        ...args.options,
      }
    );

    return result[this.getResponseKey("update")];
  }

  /**
   * Delete an entity
   */
  async delete(args: DeleteArgs<TEntity, TWhereInput>): Promise<TEntity> {
    const operation = {
      key: `${this.entityName}.delete`,
      document: this.getDeleteDocument(this.buildFieldSelection(args.select)),
    };

    const result = await this.executeQuery<{ [key: string]: TEntity }>(
      operation,
      {
        variables: { where: args.where },
        ...args.options,
      }
    );

    return result[this.getResponseKey("delete")];
  }

  protected async executeOperation<T>(
    operation: Operation,
    args: TWhereInput & QueryOptions
  ): Promise<T> {
    try {
      const subSelection = args as { select?: FieldSelection<TEntity> };

      const selection = this.buildFieldSelection(subSelection.select);
      const document = this.getOperationDocument(operation.key, selection);

      return this.executeQuery<T>(
        {
          key: operation.key,
          document,
        },
        args.variables
      );
    } catch (error) {
      this.logger?.error(`Error executing ${operation.key} operation`, {
        error,
        entityName: this.entityName,
        args,
      });
      throw error;
    }
  }

  protected getOperationDocument(
    operationKey: string,
    selection: string
  ): DocumentNode {
    const [entity, operation] = operationKey.split(".");

    switch (operation) {
      case "findMany":
        return this.getFindManyDocument(selection);
      case "findUnique":
        return this.getFindUniqueDocument(selection);
      case "create":
        return this.getCreateDocument(selection);
      case "update":
        return this.getUpdateDocument(selection);
      case "delete":
        return this.getDeleteDocument(selection);
      default:
        throw new Error(`Unknown operation: ${operation} for entity ${entity}`);
    }
  }

  // Abstract methods to be implemented by generated code
  protected abstract getFindManyDocument(selection: string): DocumentNode;
  protected abstract getFindUniqueDocument(selection: string): DocumentNode;
  protected abstract getCreateDocument(selection: string): DocumentNode;
  protected abstract getUpdateDocument(selection: string): DocumentNode;
  protected abstract getDeleteDocument(selection: string): DocumentNode;

  /**
   * Get the response key for a given operation
   */
  protected getResponseKey(operation: string): string {
    return `${operation}${this.entityName}`;
  }
}
